import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TranslateIcon from '@material-ui/icons/Translate';
import './ModernMenu.css';
import { setPendingScrollTarget } from '../../constants/navigation';

const ModernMenu = () => {
  const location = useLocation();
  const isHomeRoute = location.pathname === '/';
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('about');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuMode, setMenuMode] = useState(() => {
    if (typeof window === 'undefined') return 'full';
    // Start compact on smaller viewports to avoid a first-paint mismatch.
    return window.innerWidth <= 1200 ? 'compact' : 'full';
  }); // 'full' | 'tight' | 'compact'

  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const desktopMenuRef = useRef(null);
  const measureMenuRef = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = useMemo(
    () => [
      { id: 'about', label: t('nav.about'), color: '#00f5ff' },
      { id: 'hobbies', label: t('nav.hobbies'), color: '#c77dff' },
      { id: 'experience', label: t('nav.experience'), color: '#39ff14' },
      { id: 'stack', label: t('nav.stack'), color: '#ff6b35' },
      { id: 'recommendations', label: t('nav.recommendations'), color: '#e0aaff' },
      { id: 'contact', label: t('nav.contact'), color: '#0099ff' },
      { id: 'stayConnected', label: t('nav.stayConnected'), color: '#ff3366' },
    ],
    [t]
  );

  useEffect(() => {
    if (!isHomeRoute) {
      setActiveSection('');
    }
  }, [isHomeRoute]);

  const currentLang = (i18n.language || 'es').toLowerCase().startsWith('en') ? 'en' : 'es';
  const setLang = (lang) => {
    if (lang === currentLang) return;
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    if (menuMode !== 'compact' && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [menuMode, isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    let raf = 0;

    const computeMenuMode = () => {
      const containerEl = containerRef.current;
      const logoEl = logoRef.current;
      const menuEl = desktopMenuRef.current;
      const measureEl = measureMenuRef.current;
      const rightEl = rightRef.current;
      const viewportWidth = window.innerWidth;
      // Switch a bit earlier to avoid edge-case overlaps when labels get longer
      const forceCompactThreshold = 1200;
      const tightThreshold = 1360;

      // If refs aren't ready yet (first paint), still honor breakpoints.
      if (!containerEl || !logoEl || !menuEl || !measureEl || !rightEl) {
        if (viewportWidth <= forceCompactThreshold) return 'compact';
        if (viewportWidth < tightThreshold) return 'tight';
        return 'full';
      }

      if (viewportWidth <= forceCompactThreshold) return 'compact';

      // If the menu itself is overflowing its allocated space, go compact.
      // This is the most direct signal that labels are colliding/overlapping.
      const menuInternalOverflow = menuEl.clientWidth > 0 && menuEl.scrollWidth > menuEl.clientWidth + 1;
      if (menuInternalOverflow) return 'compact';

      // Collision detection (more reliable than width math when flexbox shrinks)
      // If menu items visually collide with logo or right controls, go compact.
      const firstLink = menuEl.querySelector('.nav-item:first-child .nav-link');
      const lastLink = menuEl.querySelector('.nav-item:last-child .nav-link');

      if (typeof logoEl.getBoundingClientRect === 'function' && typeof rightEl.getBoundingClientRect === 'function' && firstLink && lastLink) {
        const logoRect = logoEl.getBoundingClientRect();
        const rightRect = rightEl.getBoundingClientRect();
        const firstRect = firstLink.getBoundingClientRect();
        const lastRect = lastLink.getBoundingClientRect();

        const gap = 8;
        const collidesLeft = firstRect.left < logoRect.right + gap;
        const collidesRight = lastRect.right > rightRect.left - gap;

        if (collidesLeft || collidesRight) return 'compact';
      }

      const containerWidth = containerEl.clientWidth;
      const fixedWidth = logoEl.offsetWidth + rightEl.offsetWidth;
      const gutters = 64; // buffer for gaps/padding + rounding
      const available = Math.max(0, containerWidth - fixedWidth - gutters);
      const needed = Math.ceil(measureEl.getBoundingClientRect().width || measureEl.scrollWidth || 0);
      const wouldOverflow = needed > available + 1;

      if (wouldOverflow) return 'compact';
      if (viewportWidth < tightThreshold) return 'tight';
      return 'full';
    };

    const schedule = () => {
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        setMenuMode(computeMenuMode());
      });
    };

    schedule();
    window.addEventListener('resize', schedule);

    let ro;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(schedule);
      ro.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', schedule);
      window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, [i18n.language, menuItems.length]);

  const handlePendingNavigation = (sectionId, closeMenu = false) => {
    setPendingScrollTarget(sectionId);
    if (closeMenu) {
      setIsMobileMenuOpen(false);
    }
  };

  const renderLogo = () => {
    if (isHomeRoute) {
      return (
        <ScrollLink
          className="navbar-logo"
          to="about"
          spy={true}
          smooth={true}
          offset={-70}
          duration={500}
          innerRef={logoRef}
        >
          <span className="logo-bracket">&lt;</span>
          <span className="logo-text">CIAP</span>
          <span className="logo-bracket">/&gt;</span>
        </ScrollLink>
      );
    }

    return (
      <RouterLink
        className="navbar-logo"
        to="/"
        innerRef={logoRef}
        onClick={() => handlePendingNavigation('about')}
      >
        <span className="logo-bracket">&lt;</span>
        <span className="logo-text">CIAP</span>
        <span className="logo-bracket">/&gt;</span>
      </RouterLink>
    );
  };

  const renderDesktopNavLink = (item) => {
    const content = (
      <>
        <span className="nav-link-text">{item.label}</span>
        <span className="nav-link-underline"></span>
      </>
    );

    if (!isHomeRoute) {
      return (
        <RouterLink
          className="nav-link"
          to="/"
          style={{ '--item-color': item.color }}
          onClick={() => handlePendingNavigation(item.id)}
        >
          {content}
        </RouterLink>
      );
    }

    return (
      <ScrollLink
        className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
        to={item.id}
        spy={true}
        smooth={true}
        offset={-70}
        duration={500}
        onSetActive={() => setActiveSection(item.id)}
        style={{
          '--item-color': item.color
        }}
      >
        {content}
      </ScrollLink>
    );
  };

  const renderMobileNavLink = (item) => {
    if (!isHomeRoute) {
      return (
        <RouterLink
          to="/"
          className="mobile-nav-link"
          onClick={() => handlePendingNavigation(item.id, true)}
        >
          {item.label}
        </RouterLink>
      );
    }

    return (
      <ScrollLink
        className="mobile-nav-link"
        to={item.id}
        spy={true}
        smooth={true}
        offset={-70}
        duration={500}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        {item.label}
      </ScrollLink>
    );
  };

  return (
    <>
      <nav className={`modern-navbar ${isScrolled ? 'scrolled' : ''} ${menuMode}`}>
        <div className="navbar-container" ref={containerRef}>
          {/* Logo */}
          {renderLogo()}

          {/* Desktop Menu (same level; auto-hides when overflow -> compact) */}
          <ul className="navbar-menu desktop-menu" ref={desktopMenuRef}>
            {menuItems.map((item) => (
              <li key={item.id} className="nav-item">
                {renderDesktopNavLink(item)}
              </li>
            ))}
          </ul>

          {/* Hidden measurement menu (used to compute required width reliably) */}
          <ul className="navbar-menu desktop-menu measure-menu" ref={measureMenuRef} aria-hidden="true">
            {menuItems.map((item) => (
              <li key={item.id} className="nav-item">
                <span className="nav-link">
                  <span className="nav-link-text">{item.label}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="navbar-right" ref={rightRef}>
            {/* Language Toggle (desktop/tight only; on compact it moves into mobile menu) */}
            {menuMode !== 'compact' && (
              <div className="language-switch" role="group" aria-label="Language">
                <TranslateIcon className="language-icon" aria-hidden="true" focusable="false" />
                <button
                  type="button"
                  className={`lang-btn ${currentLang === 'es' ? 'active' : ''}`}
                  onClick={() => setLang('es')}
                >
                  ES
                </button>
                <button
                  type="button"
                  className={`lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                  onClick={() => setLang('en')}
                >
                  EN
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              type="button"
              className={`mobile-menu-btn ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden={!isMobileMenuOpen}
        />

        {/* Mobile Menu */}
        <div id="mobile-menu" className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} role="dialog" aria-modal="true" aria-label="Menu">
          <div className="mobile-menu-header">
            <button
              type="button"
              className="mobile-menu-close"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <div className="mobile-menu-content">
            <ul className="mobile-menu-list">
              {menuMode === 'compact' && (
                <li className="mobile-menu-item mobile-lang" style={{ '--delay': '0s' }}>
                  <div className="language-switch" role="group" aria-label="Language">
                    <TranslateIcon className="language-icon" aria-hidden="true" focusable="false" />
                    <button
                      type="button"
                      className={`lang-btn ${currentLang === 'es' ? 'active' : ''}`}
                      onClick={() => setLang('es')}
                    >
                      ES
                    </button>
                    <button
                      type="button"
                      className={`lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                      onClick={() => setLang('en')}
                    >
                      EN
                    </button>
                  </div>
                </li>
              )}
              {menuItems.map((item, index) => (
                <li
                  key={item.id}
                  className="mobile-menu-item"
                  style={{
                    '--delay': `${(index + (menuMode === 'compact' ? 1 : 0)) * 0.1}s`,
                    '--item-color': item.color
                  }}
                >
                  {renderMobileNavLink(item)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default ModernMenu;
